import uniq from 'lodash/uniq';
import takeRight from 'lodash/takeRight';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import NoSsr from '@material-ui/core/NoSsr';
import LocalStorage, { STORAGE_KEYS } from '../../services/localStorage';
import { getSocketIfExist } from '../../utils/getSocket';
import { log } from '../../utils/log';
import { sendUserQueryMessage } from '../../utils/ws-v1';
const adaptAvaName = (avaName) => {
    if (!avaName) {
        return '';
    }
    return avaName.replace(/[^A-Z0-9]/gi, '').toLowerCase();
};
const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
    },
    input: {
        display: 'flex',
        padding: 0,
        height: '100%',
        minWidth: 192,
        fontSize: 18,
        color: theme.palette.ava.blue2,
    },
}));
const RoomAutoComplete = ({ onChange, onAvaName, components, autoFocus }) => {
    const classes = useStyles();
    const theme = useTheme();
    const [inputValue, setInputValue] = useState('');
    const [fullValue, setFullValue] = useState(undefined);
    const [cache, setCache] = useState({});
    const [users, setUsers] = useState({});
    const updateCache = (users) => {
        setUsers((currentUsers) => (Object.assign(Object.assign({}, currentUsers), users.reduce((total, user) => {
            total[user.avaName] = user;
            return total;
        }, {}))));
        const updatedCache = cache;
        users.forEach((user) => {
            for (let i = 0; i <= user.avaName.length; i += 1) {
                updatedCache[user.avaName.slice(0, i)] = uniq((updatedCache[user.avaName.slice(0, i)] || []).concat([`&${user.avaName}`])).slice(0, 3);
            }
        });
        setCache(updatedCache);
    };
    const handleUserQueryResponse = (message) => {
        const data = JSON.parse(message.data);
        log('websocketMessage', 'info', { message: data, direction: 'b2f' });
        if (data.type === 'user-query-response') {
            updateCache(data.users);
        }
    };
    useEffect(() => {
        const localStorageCache = LocalStorage.get(STORAGE_KEYS.CACHE, {});
        updateCache(Array.from(Object.values(localStorageCache)));
        let request = LocalStorage.get(STORAGE_KEYS.AVA_REQUEST);
        if (request) {
            request = `&${request}`;
            setFullValue({
                value: request,
                label: request,
                user: { avaName: request.slice(1), userName: '' },
            });
            setInputValue('');
            onChange(request);
        }
        const ws = getSocketIfExist();
        if (ws) {
            ws.addEventListener('message', handleUserQueryResponse);
        }
        return () => {
            if (ws) {
                ws.removeEventListener('message', handleUserQueryResponse);
            }
        };
    }, []);
    const handleInputChange = (avaNameInput) => {
        let avaName = adaptAvaName(avaNameInput);
        if (!avaName) {
            avaName = '';
        }
        setInputValue(avaName);
        if (avaName) {
            onChange(`&${avaName}`);
        }
        const ws = getSocketIfExist();
        if (ws && avaName) {
            sendUserQueryMessage(ws, avaName);
        }
    };
    const updateLocalStorageCache = (value) => {
        let localStorageCache = LocalStorage.get(STORAGE_KEYS.CACHE, {});
        // Store 4 users max in the cache
        if (Array.from(Object.keys(localStorageCache)).length > 3) {
            localStorageCache = Object.assign({}, takeRight(Array.from(Object.values(localStorageCache)), 3).reduce((total, user) => {
                total[user.avaName] = user;
                return total;
            }, {}));
        }
        localStorageCache[value.user.avaName] = value.user;
        LocalStorage.set(STORAGE_KEYS.CACHE, localStorageCache);
    };
    const { [inputValue]: suggestionsAvaName } = cache;
    const suggestions = (suggestionsAvaName || []).map((s) => users[s.slice(1)]);
    const formattedSuggestions = inputValue
        ? suggestions.sort().map((user) => ({
            label: user.userName,
            value: `&${user.avaName}`,
            user,
        }))
        : [];
    const customS = {
        value: `&${inputValue}`,
        label: `&${inputValue}`,
        user: { avaName: inputValue, userName: '' },
    };
    if (inputValue !== '') {
        if (!formattedSuggestions.filter((f) => f.value === customS.value).length) {
            formattedSuggestions.push(customS);
        }
    }
    if (formattedSuggestions.length === 0 && fullValue) {
        formattedSuggestions.push(fullValue);
    }
    return (React.createElement("div", { className: classes.root },
        React.createElement(NoSsr, null,
            React.createElement(Select, { classes: classes, styles: {
                    input: (base) => (Object.assign(Object.assign({}, base), { color: theme.palette.ava.blue2, '& input': {
                            font: 'inherit',
                        } })),
                }, textFieldProps: {
                    InputLabelProps: {
                        shrink: true,
                    },
                }, inputValue: inputValue, value: fullValue, onInputChange: (e, a) => {
                    if (a && a.action === 'input-blur' && inputValue) {
                        setFullValue(customS);
                        setInputValue('');
                        return;
                    }
                    handleInputChange(e);
                }, onChange: (e, action) => {
                    if (fullValue && action && action.action === 'clear') {
                        setFullValue(undefined);
                        setInputValue(fullValue.value);
                        onChange(fullValue.value);
                    }
                    else if (e) {
                        setFullValue(e);
                        setInputValue('');
                        onChange(e.value);
                        updateLocalStorageCache(e);
                        onAvaName(e.value);
                    }
                }, autoFocus: autoFocus, options: formattedSuggestions, components: components, isClearable: true }))));
};
export default RoomAutoComplete;
